import { createSelector } from 'reselect';

import { orgsForAccountId } from './org-selectors';
import { EMPLOYEE_STATUS, EmployeeStatus } from '../employees/actions/load-org-employees-by-status';

import { RequestState, StateDictionary } from './utils/state-manager';

type EmployeeDictionary = { [employeeId: number]: Employee };
type EmployeeNotificationDictionary = { [orgId: number]: Array<EmployeeTextNotification> };

export type EmployeeState = {
  employeesById: EmployeeDictionary;
  employeesStateById: StateDictionary;
  employeesStateByOrgIdAndStatus: { [orgIdStatus: string]: RequestState };
  employeeNotificationsByOrgId: EmployeeNotificationDictionary;
  employeeNotificationsByOrgIdStatus: StateDictionary;
  employeeSummaryByOrgId: { [orgId: number]: EmployeeSummaryData };
  employeeSummaryStateByOrgId: StateDictionary;
  endEmploymentReasons: Array<EndEmploymentReason> | null;
  endEmploymentReasonsStatus: RequestState;
};
type State = { employees: EmployeeState };

const employeesByIdSelector = (state: State) => state.employees.employeesById;
export const employeeSelector = createSelector(
  [employeesByIdSelector, (_state: State, employeeId: number) => employeeId],
  (employeesById, employeeId) => employeesById[employeeId],
);
export const employeeForJobApplicantIdSelector = createSelector(
  [employeesByIdSelector, (_state: State, jobApplicantId: number) => jobApplicantId],
  (employeesById, jobApplicantId) =>
    Object.values(employeesById).find((e) => e.jobApplicantId === jobApplicantId),
);

export const employeesForOrgIdSelector = createSelector(
  [employeesByIdSelector, (_state: State, orgId: number) => orgId],
  (employeesById, orgId) => Object.values(employeesById).filter((e) => e.orgId === orgId),
);

export const employeesStateByOrgIdAndStatusSelector = (state: State) =>
  state.employees.employeesStateByOrgIdAndStatus;

export const employeesStateForOrgIdAndStatusSelector = createSelector(
  [
    employeesStateByOrgIdAndStatusSelector,
    (_state: State, orgId: number) => orgId,
    (_state: State, _orgId: number, status: EmployeeStatus) => status,
  ],
  (applicationsStateByOrgIdAndStatus, orgId, status) =>
    applicationsStateByOrgIdAndStatus[`${orgId}-${status}`],
);

export const employeesStateForOrgIdAndAllStatusSelector = createSelector(
  [(state: State) => state, (_state: State, orgId: number) => orgId],
  (state, orgId) =>
    [EMPLOYEE_STATUS.CURRENT, EMPLOYEE_STATUS.FORMER].map((status: string) =>
      employeesStateForOrgIdAndStatusSelector(state, orgId, status),
    ),
);

const orgsForAccountIdSelector = (state: any, accountId: number) =>
  orgsForAccountId(state, accountId);
export const employeesForAccountIdSelector = createSelector(
  [
    employeesByIdSelector,
    orgsForAccountIdSelector,
    (_state: State, accountId: number) => accountId,
  ],
  (employeesById, orgsInAccount) => {
    const orgIdsInAccount = new Set(orgsInAccount?.map((o) => o.org_id) || []);
    return Object.values(employeesById).filter((e) => orgIdsInAccount.has(e.orgId));
  },
);

const employeesStateByIdSelector = (state: State) => state.employees.employeesStateById;
export const employeesStateForIdSelector = createSelector(
  [employeesStateByIdSelector, (_state: State, employeeId: number) => employeeId],
  (employeesStateById, employeeId) => employeesStateById[employeeId],
);

export const employeeNotificationsForOrgIdSelector = createSelector(
  [
    (state: State) => state.employees.employeeNotificationsByOrgId,
    (_state: State, orgId: number) => orgId,
  ],
  (employeeNotificationsByOrgId, orgId) =>
    employeeNotificationsByOrgId ? employeeNotificationsByOrgId[orgId] : null,
);

export const employeeNotificationsByOrgIdStatusSelector = (state: State) =>
  state.employees.employeeNotificationsByOrgIdStatus;
export const employeeNotificationStateForOrgIdSelector = createSelector(
  [employeeNotificationsByOrgIdStatusSelector, (_state: State, orgId: number) => orgId],
  (employeeNotificationsByOrgId, orgId) =>
    employeeNotificationsByOrgId ? employeeNotificationsByOrgId[orgId] : null,
);

export const employeesForGroupIdSelector = createSelector(
  [employeesByIdSelector, (_state: State, employeeGroupId: number) => employeeGroupId],
  (employeesById, employeeGroupId) =>
    Object.values(employeesById).filter((e) =>
      e.EmployeeGroups.map((g) => g.employeeGroupId).includes(employeeGroupId),
    ),
);

export const activeEmployeesForGroupIdSelector = createSelector(
  [employeesByIdSelector, (_state: State, employeeGroupId: number) => employeeGroupId],
  (employeesById, employeeGroupId) =>
    Object.values(employeesById).filter(
      (e) =>
        e.EmployeeGroups.map((g) => g.employeeGroupId).includes(employeeGroupId) &&
        e.isCurrentEmployee,
    ),
);

const employeeSummaryByOrgIdSelector = (state: State) => state.employees.employeeSummaryByOrgId;
export const employeeSummaryForOrgIdSelector = createSelector(
  [employeeSummaryByOrgIdSelector, (_state: State, orgId: number) => orgId],
  (employeeSummaryByOrgId, orgId) =>
    employeeSummaryByOrgId ? employeeSummaryByOrgId[orgId] : null,
);

const employeeSummaryStateByOrgIdSelector = (state: State) =>
  state.employees.employeeSummaryStateByOrgId;
export const employeeSummaryStateForOrgIdSelector = createSelector(
  [employeeSummaryStateByOrgIdSelector, (_state: State, orgId: number) => orgId],
  (employeeSummaryStateByOrgId, orgId) =>
    employeeSummaryStateByOrgId ? employeeSummaryStateByOrgId[orgId] : null,
);

export const endEmploymentReasonsStatusSelector = (state: State) =>
  state.employees.endEmploymentReasonsStatus;
